import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';

const Campaigns = () => {
  const { companyId, projectId } = useParams();
  const [company, setCompany] = useState(null);
  const [project, setProject] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    campaign_type: 'email',
    channel: '',
    budget: '',
    start_date: '',
    end_date: '',
    status: 'draft'
  });
  const navigate = useNavigate();

  useEffect(() => {
    fetchCompany();
    fetchProject();
    fetchCampaigns();
  }, [companyId, projectId]);

  const fetchCompany = async () => {
    try {
      const response = await api.get(`/companies/${companyId}`);
      setCompany(response.data);
    } catch (error) {
      console.error('Error fetching company:', error);
    }
  };

  const fetchProject = async () => {
    try {
      const response = await api.get(`/companies/${companyId}/projects/${projectId}`);
      setProject(response.data);
    } catch (error) {
      console.error('Error fetching project:', error);
    }
  };

  const fetchCampaigns = async () => {
    try {
      const response = await api.get(`/projects/${projectId}/campaigns`);
      setCampaigns(response.data);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      setError('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCampaign = async (e) => {
    e.preventDefault();
    try {
      const response = await api.post(`/projects/${projectId}/campaigns`, {
        ...formData,
        budget: formData.budget ? parseFloat(formData.budget) : null
      });
      
      const newCampaign = response.data;
      setCampaigns([...campaigns, newCampaign]);
      setFormData({
        name: '',
        slug: '',
        description: '',
        campaign_type: 'email',
        channel: '',
        budget: '',
        start_date: '',
        end_date: '',
        status: 'draft'
      });
      setShowCreateForm(false);
    } catch (error) {
      console.error('Error creating campaign:', error);
      setError('Failed to create campaign');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
      ...(name === 'name' && { slug: value.toLowerCase().replace(/[^a-z0-9]+/g, '-') })
    }));
  };

  const getStatusBadge = (status) => {
    const colors = {
      draft: 'badge-secondary',
      scheduled: 'badge-info',
      running: 'badge-success',
      paused: 'badge-warning',
      completed: 'badge-primary',
      cancelled: 'badge-danger'
    };
    return <span className={`badge ${colors[status] || 'badge-secondary'}`}>{status}</span>;
  };

  const getCampaignTypeIcon = (type) => {
    const icons = {
      email: '📧',
      social: '📱',
      paid_ads: '💰',
      seo: '🔍',
      content: '📝',
      webinar: '🎥',
      event: '🎪',
      influencer: '⭐'
    };
    return icons[type] || '📊';
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'No budget';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  const calculateROI = (campaign) => {
    if (!campaign.cost || campaign.cost === 0) return 'N/A';
    const roi = ((campaign.revenue - campaign.cost) / campaign.cost) * 100;
    return `${roi.toFixed(1)}%`;
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px'
      }}>
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: '2rem' }}>
        <nav className="breadcrumb">
          <button
            onClick={() => navigate(`/companies/${companyId}/projects`)}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--primary-600)',
              cursor: 'pointer',
              fontSize: '0.875rem',
              marginRight: '1rem'
            }}
          >
            ← Back to {company?.name} Projects
          </button>
        </nav>
        
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <h1 style={{
              color: 'var(--gray-900)',
              fontSize: '2rem',
              fontWeight: 'bold',
              margin: '0 0 0.5rem 0'
            }}>
              🚀 {project?.name} Campaigns
            </h1>
            <p style={{
              color: 'var(--gray-600)',
              margin: 0
            }}>
              Marketing campaigns for {project?.name} in {company?.name}
            </p>
          </div>
          <button
            onClick={() => setShowCreateForm(true)}
            style={{
              backgroundColor: 'var(--primary-600)',
              color: 'white',
              border: 'none',
              padding: '0.75rem 1.5rem',
              borderRadius: '8px',
              fontSize: '0.875rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'background-color 0.2s'
            }}
          >
            + Add Campaign
          </button>
        </div>
      </div>

      {error && (
        <div className="card" style={{
          backgroundColor: 'var(--red-50)',
          borderColor: 'var(--red-200)',
          color: 'var(--red-800)',
          marginBottom: '1.5rem'
        }}>
          <p style={{ margin: 0 }}>{error}</p>
        </div>
      )}

      {showCreateForm && (
        <div className="card" style={{ marginBottom: '2rem' }}>
          <h3 style={{ margin: '0 0 1rem 0', color: 'var(--gray-900)' }}>Create New Campaign</h3>
          <form onSubmit={handleCreateCampaign}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: 'var(--gray-700)' }}>
                  Campaign Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="form-input"
                  placeholder="Enter campaign name"
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: 'var(--gray-700)' }}>
                  Slug
                </label>
                <input
                  type="text"
                  name="slug"
                  value={formData.slug}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="campaign-slug"
                />
              </div>
            </div>
            
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: 'var(--gray-700)' }}>
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                className="form-input"
                rows="3"
                placeholder="Brief description of the campaign"
              />
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: 'var(--gray-700)' }}>
                  Campaign Type *
                </label>
                <select
                  name="campaign_type"
                  value={formData.campaign_type}
                  onChange={handleInputChange}
                  className="form-input"
                  required
                >
                  <option value="email">Email Marketing</option>
                  <option value="social">Social Media</option>
                  <option value="paid_ads">Paid Advertising</option>
                  <option value="seo">SEO/Organic</option>
                  <option value="content">Content Marketing</option>
                  <option value="webinar">Webinar</option>
                  <option value="event">Event</option>
                  <option value="influencer">Influencer</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: 'var(--gray-700)' }}>
                  Channel/Platform
                </label>
                <input
                  type="text"
                  name="channel"
                  value={formData.channel}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="e.g., Facebook, Google, LinkedIn"
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: 'var(--gray-700)' }}>
                  Budget
                </label>
                <input
                  type="number"
                  name="budget"
                  value={formData.budget}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="5000"
                  min="0"
                  step="0.01"
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: 'var(--gray-700)' }}>
                  Status
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  className="form-input"
                >
                  <option value="draft">Draft</option>
                  <option value="scheduled">Scheduled</option>
                  <option value="running">Running</option>
                  <option value="paused">Paused</option>
                  <option value="completed">Completed</option>
                </select>
              </div>
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: 'var(--gray-700)' }}>
                  Start Date
                </label>
                <input
                  type="datetime-local"
                  name="start_date"
                  value={formData.start_date}
                  onChange={handleInputChange}
                  className="form-input"
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: 'var(--gray-700)' }}>
                  End Date
                </label>
                <input
                  type="datetime-local"
                  name="end_date"
                  value={formData.end_date}
                  onChange={handleInputChange}
                  className="form-input"
                />
              </div>
            </div>
            
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setShowCreateForm(false)}
                style={{
                  backgroundColor: 'var(--gray-200)',
                  color: 'var(--gray-700)',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                style={{
                  backgroundColor: 'var(--primary-600)',
                  color: 'white',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              >
                Create Campaign
              </button>
            </div>
          </form>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '1.5rem' }}>
        {campaigns.map((campaign) => (
          <div key={campaign.id} className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{ fontSize: '1.5rem' }}>{getCampaignTypeIcon(campaign.campaign_type)}</span>
                <div>
                  <h3 style={{ margin: '0 0 0.25rem 0', color: 'var(--gray-900)', fontSize: '1.25rem', fontWeight: 'bold' }}>
                    {campaign.name}
                  </h3>
                  <p style={{ margin: 0, color: 'var(--gray-600)', fontSize: '0.875rem' }}>
                    {campaign.campaign_type.replace('_', ' ')} {campaign.channel && `• ${campaign.channel}`}
                  </p>
                </div>
              </div>
              {getStatusBadge(campaign.status)}
            </div>
            
            {campaign.description && (
              <p style={{ 
                color: 'var(--gray-700)', 
                fontSize: '0.9rem', 
                lineHeight: '1.5',
                marginBottom: '1rem',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden'
              }}>
                {campaign.description}
              </p>
            )}
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div style={{ fontSize: '0.85rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <span className="breadcrumb-current">Budget:</span>
                  <span style={{ fontWeight: 'bold', color: 'var(--gray-900)' }}>
                    {formatCurrency(campaign.budget)}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <span className="breadcrumb-current">Spent:</span>
                  <span style={{ fontWeight: 'bold', color: 'var(--red-600)' }}>
                    {formatCurrency(campaign.cost)}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span className="breadcrumb-current">Revenue:</span>
                  <span style={{ fontWeight: 'bold', color: 'var(--green-600)' }}>
                    {formatCurrency(campaign.revenue)}
                  </span>
                </div>
              </div>
              <div style={{ fontSize: '0.85rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <span className="breadcrumb-current">Impressions:</span>
                  <span style={{ fontWeight: 'bold', color: 'var(--gray-900)' }}>
                    {campaign.impressions?.toLocaleString() || '0'}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <span className="breadcrumb-current">Clicks:</span>
                  <span style={{ fontWeight: 'bold', color: 'var(--blue-600)' }}>
                    {campaign.clicks?.toLocaleString() || '0'}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span className="breadcrumb-current">ROI:</span>
                  <span style={{ 
                    fontWeight: 'bold', 
                    color: campaign.revenue > campaign.cost ? 'var(--green-600)' : 'var(--red-600)' 
                  }}>
                    {calculateROI(campaign)}
                  </span>
                </div>
              </div>
            </div>
            
            <div style={{ paddingTop: '1rem', borderTop: '1px solid var(--gray-200)', fontSize: '0.8rem', color: 'var(--gray-600)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Created: {new Date(campaign.created_at).toLocaleDateString()}</span>
                {campaign.start_date && (
                  <span>Starts: {new Date(campaign.start_date).toLocaleDateString()}</span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {campaigns.length === 0 && !loading && (
        <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
          <h3 style={{ color: 'var(--gray-600)', margin: '0 0 1rem 0' }}>No Campaigns Yet</h3>
          <p style={{ color: 'var(--gray-500)', marginBottom: '2rem' }}>
            Create your first campaign to start marketing activities for {project?.name}.
          </p>
          <button
            onClick={() => setShowCreateForm(true)}
            style={{
              backgroundColor: 'var(--primary-600)',
              color: 'white',
              border: 'none',
              padding: '0.75rem 1.5rem',
              borderRadius: '8px',
              fontSize: '0.875rem',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            + Create First Campaign
          </button>
        </div>
      )}
    </div>
  );
};

export default Campaigns;